import get from 'lodash/get';
import find from 'lodash/find';
import includes from 'lodash/includes';
import { createSelector } from 'reselect';
import * as actionTypes from './actionTypes';
import * as constants from './constants';
/**
 * Input selectors
 */ const mainSelector = (state)=>state[constants.MODULE_ID];
const fetchingSelector = (state)=>state[constants.MODULE_ID].fetching;
/**
 * Selects if client re-renders app after cold start
 */ export const isClientInitialized = createSelector(mainSelector, (state)=>state.isClientInitialized);
/**
 * Selects current region name
 */ export const regionName = createSelector(mainSelector, (state)=>state.regionName);
/**
 * Selects original region name
 */ export const originalRegionName = createSelector(mainSelector, (state)=>state.originalRegionName);
/**
 * Regions dictionary
 */ export const regions = createSelector(mainSelector, (state)=>state.regions);
/**
 * Selects current region
 */ export const region = createSelector(regionName, regions, (regionNameState, regionsState)=>find(regionsState, {
        name: regionNameState
    }));
/**
 * Selects country context
 */ export const country = createSelector(mainSelector, (state)=>state.country);
/**
 * Selects html direction
 */ export const direction = createSelector(mainSelector, (state)=>state.direction);
/**
 * Selects locale setting
 */ export const locale = createSelector(mainSelector, (state)=>state.locale);
/**
 * Selects language code setting
 */ export const langCode = createSelector(mainSelector, (state)=>constants.APP_LOCALE_LANGUAGES_MAP[state.locale] || state.locale);
/**
 * Selects translations for the language code
 */ export const translations = createSelector(mainSelector, (state)=>state.translations);
/**
 * Countries dictionary
 */ export const countries = createSelector(mainSelector, (state)=>state.countries);
/**
* Selector to find country by alpha2Code
* @param {string} alpha2Code - The country code to search for
*/ export const countryByAlpha2Code = (alpha2Code)=>createSelector(countries, (countriesState)=>countriesState.find((countryItem)=>countryItem.alpha2Code === alpha2Code));
/**
 * Sign up chosen country
 */ export const signUpCountryCode = createSelector(mainSelector, (state)=>state.signUpCountryCode);
/**
 * Devices list
 */ export const devices = createSelector(mainSelector, (state)=>state.devices);
/**
 * Devices case types list
 */ export const caseTypes = createSelector(mainSelector, (state)=>state.caseTypes);
/**
 * Languages dictionary
 */ export const languages = createSelector(mainSelector, (state)=>state.languages);
/**
 * Selects current language
 */ export const language = createSelector(languages, langCode, (languagesState, langCodeState)=>find(languagesState, {
        code: langCodeState
    }));
/**
 * Localization resources
 */ export const localizationResources = createSelector(mainSelector, (state)=>state.localizationResources);
/**
 * Localization resources
 */ export const localizationResourceByResourceKey = (resourceKey)=>createSelector(localizationResources, (localizationResourcesState)=>find(localizationResourcesState, {
            resourceKey
        }));
/**
 * Alerts selector
 */ export const alerts = createSelector(mainSelector, (state)=>state.alerts);
/**
 * System alerts selector
 */ export const systemAlerts = createSelector(mainSelector, (state)=>state.systemAlerts);
/**
 * System alerts settings selector
 */ export const systemAlertsSettings = createSelector(mainSelector, (state)=>state.systemAlertsSettings);
/**
 * Alerts that are generated before time threshold are obsolete
 */ export const alertsTimeThreshold = createSelector(mainSelector, (state)=>state.alertsTimeThreshold);
/**
 * Dismissed alerts by alert name
 */ export const dismissedAlerts = createSelector(mainSelector, (state)=>state.dismissedAlerts);
/**
 * Forms states
 */ export const formSelector = (formName)=>createSelector(mainSelector, (state)=>get(state.forms, formName, null));
export const formFieldValueSelector = (formName, fieldId)=>createSelector([
        (state)=>get(state, `${constants.MODULE_ID}.forms.${formName}.values.${fieldId}`)
    ], (fieldValueState)=>fieldValueState);
export const formFieldErrorsSelector = (formName, fieldId)=>createSelector([
        (state)=>get(state, `${constants.MODULE_ID}.forms.${formName}.errors.${fieldId}`, null)
    ], (fieldErrorsState)=>fieldErrorsState);
export const formFieldBEErrorsSelector = (formName, fieldId)=>createSelector([
        (state)=>get(state, `${constants.MODULE_ID}.forms.${formName}.BEValidationErrors.${fieldId}`, null)
    ], (fieldErrorsState)=>fieldErrorsState);
/**
 * Select dropdown state
 */ export const dropdown = createSelector(mainSelector, (state)=>state.openDropdownId);
/**
 * Print mode
 */ export const printMode = createSelector(mainSelector, (state)=>state.printMode);
/**
 * Current route data
 */ export const route = createSelector(mainSelector, (state)=>state.route);
/**
 * Select modal state
 */ export const modal = createSelector(mainSelector, (state)=>state.openModalId);
/**
 * Select floating modal state
 */ export const floatingModalSelector = (floatingModalId)=>createSelector(mainSelector, (state)=>find(state.openFloatingModals, {
            floatingModalId
        }, null));
/**
 * Select active floating modal
 */ export const activeFloatingModalId = createSelector(mainSelector, (state)=>state.activeFloatingModalId);
/**
 * Feature toggles
 */ export const featureToggles = createSelector(mainSelector, (state)=>state.featureToggles);
export const isSignalRConnected = createSelector(mainSelector, (state)=>state.isSignalRConnected);
/**
 * Websocket state
 */ export const websocketStateSelector = ()=>createSelector(mainSelector, (state)=>state.wsState);
//----------------------------------------------------------------------------------------------------------------------
export const isLocalizationResourcesInProgress = createSelector(fetchingSelector, (state)=>includes(state, actionTypes.FETCH_LOCALIZATION_RESOURCES));
export const isNavigationBlocked = createSelector(mainSelector, (state)=>state.isNavigationBlocked);
export const allowSignUp = createSelector(mainSelector, (state)=>get(state.idpClientConfiguration, 'allowSignUp', false));
export const allowReturnToPartner = createSelector(mainSelector, (state)=>get(state.idpClientConfiguration, 'allowReturnToPartner', false));
export const isIntegrationProcess = createSelector(mainSelector, (state)=>get(state.idpClientConfiguration, 'isIntegrationProcess', false));
export const isTrustedClientFlow = createSelector([
    (state)=>mainSelector(state).idpClientConfiguration
], (idpClientConfigurationState)=>get(idpClientConfigurationState, 'isTrustedClientFlow', false));
export const requireConsent = createSelector(mainSelector, (state)=>get(state.idpClientConfiguration, 'requireConsent', false));
export const idpClientConfiguration = createSelector(mainSelector, (state)=>state.idpClientConfiguration);
/**
 * displayMode state
 */ export const isMobileDisplayMode = createSelector([
    (state)=>mainSelector(state).displayMode
], (displayModeState)=>displayModeState === 'mobile');
/**
 * gets current staticFilesDir
 */ export const staticFilesDir = createSelector(mainSelector, (state)=>state.staticFilesDir);
